// @ts-nocheck
import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';
import { sectionRenderer } from '@theme/section-renderer';

const DEFAULT_TRANSLATIONS = {
  product_count_one: 'Showing {{ num }} of {{ total }} product',
  product_count_other: 'Showing {{ num }} of {{ total }} products',
  item_count: '__COUNT__ items',
};

/**
 * Manages product grid with deduplication and pagination.
 *
 * MODES:
 * - Dedup ON: Shows one variant per product, removes duplicates by title
 * - Dedup OFF: Shows all variants, uses standard Shopify pagination
 * 
 * PAGE STATES:
 * - Page 1: Auto-loads to show 24 products minimum, hides excess
 * - Page 2+: Loads all previous pages, shows all loaded products
 * 
 * KEY FEATURES:
 * - Smart "Load More" button visibility (checks next page for unique products)
 * - Parallel page loading for faster performance on page 2+
 * - Dynamic product count updates ("X items" or "Showing X of Y products")
 * - Loading skeleton grid toggle during filter updates
 * - Eager loading for first 4 product images
 * 
 * PERFORMANCE:
 * - Lazy loads images beyond first 4 products
 */
export default class ResultsList extends PaginatedList {
  #contentObserver = null;
  #allProductsLoaded = false;
  #lastCountText = null;
  #yotpoRefreshScheduled = false;
  #yotpoRefreshTimeout = null;
  #lastYotpoRefreshTime = 0;
  #yotpoRefreshCooldown = 1000;
  #isHandlingLoadMore = false;
  #lastLoadMoreFinishedAt = 0;
  static LOAD_MORE_COOLDOWN_MS = 600;

  _isInitializing = true;
  _isUpdatingCount = false;
  _translations = null;

  beginLoading() {
    this._setGridLoading(true);
  }

  endLoading() {
    this._setGridLoading(false);
  }

  _getVisibleCount() {
    const { grid } = this.refs;
    if (!grid || Array.isArray(grid)) return 0;
    
    const isSearchResults = this.hasAttribute('data-is-search-results');
    if (isSearchResults) {
      return grid.querySelectorAll('.product-grid__item').length;
    }
    
    return grid.querySelectorAll('.product-grid__item:not([data-hidden])').length;
  }

  _setGridLoading(on) {
    const { grid } = this.refs;
    if (!grid || Array.isArray(grid)) return;
    
    const loadingGrid = grid.closest('.collection-wrapper')?.querySelector('.product-grid--loading');
    
    if (on) {
      if (loadingGrid) {
        loadingGrid.removeAttribute('hidden');
        loadingGrid.setAttribute('aria-hidden', 'false');
      }
      grid.classList.add('hidden');
      grid.setAttribute('aria-hidden', 'true');
    } else {
      if (loadingGrid) {
        loadingGrid.setAttribute('hidden', '');
        loadingGrid.setAttribute('aria-hidden', 'true');
      }
      grid.classList.remove('hidden');
      grid.removeAttribute('aria-hidden');
    }
  }

  _shouldTopUp({ visible, canFetchMore }) {
    if (!canFetchMore) return false;
    return visible < this.productsPerPage;
  }

  /**
   * Returns current page number from URL (default 1). Pass a URL instance to read from that instead of window.location.
   */
  _getCurrentPageFromURL(url) {
    const u = url ?? new URL(window.location.href);
    return parseInt(u.searchParams.get('page') || '1', 10);
  }

  async connectedCallback() {
    super.connectedCallback();

    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }

    mediaQueryLarge.addEventListener('change', this._handleMediaQueryChange);
    this.setAttribute('initialized', '');

    this._translations = this._getTranslations();
    this.showSingleProduct = this.dataset.showSingleProduct === 'true';
    this.productsPerPage = parseInt(this.dataset.productsPerPage || '24');
    this.seenTitles = new Set();
    this.seenProductUrls = null;

    const isSearchResults = this.hasAttribute('data-is-search-results');
    const currentPage = this._getCurrentPageFromURL();

    // Show loading immediately when landing on page > 1 (e.g. browser back) so we don't flash partial content
    if (currentPage > 1) {
      this.beginLoading();
    }

    this._initializeSeenTitles();
    this._setupLoadMoreButton();
    this._setupContentObserver();
    this._updateButtonStateFromURL();
    this._applyEagerLoading();

    if (isSearchResults) {
      setTimeout(() => {
        const loadMoreButton = this.refs.loadMoreButton;
        if (loadMoreButton && !Array.isArray(loadMoreButton)) {
          const btnPage = parseInt(loadMoreButton.dataset.currentPage || '1');
          const totalPages = parseInt(loadMoreButton.dataset.totalPages || '1');
          if (btnPage < totalPages) {
            loadMoreButton.disabled = false;
            loadMoreButton.removeAttribute('disabled');
            loadMoreButton.style.display = 'flex';
          }
        }
      }, 100);
    }

    if (currentPage > 1) {
      await this._loadAllPreviousPages();
    }

    if (this.showSingleProduct && currentPage > 1 && !isSearchResults) {
      const loadMoreBtn = this.querySelector('.load-more-button');
      const totalPages = parseInt(loadMoreBtn?.dataset.totalPages || '1');
      
      this._checkNextPageForUniqueProducts(currentPage + 1, totalPages).then((hasUniqueProducts) => {
        if (!hasUniqueProducts) {
          this.#allProductsLoaded = true;
          if (loadMoreBtn) loadMoreBtn.style.display = 'none';
        }
      });
    }
    
    if (this.showSingleProduct) {
      await this._ensureMinimumProductsOnLoad();
      
      if (currentPage === 1) {
        const visibleAfterAutoLoad = this._getVisibleCount();
        if (visibleAfterAutoLoad > this.productsPerPage) {
          this._hideExcessProducts();
          this._updateProductCount();
        }
      }
    }
    
    this._updateProductCount();
    this._updateButtonStateFromURL();
    this.endLoading();
    
    requestAnimationFrame(() => {
      this._updateProductCount();
      // Delay initial Yotpo refresh to allow page to settle and prevent flash
      requestIdleCallback(() => {
        this.#refreshYotpoWidgets('connected');
      }, { timeout: 500 });
    });
    
    this._isInitializing = false;
  }

  constructor() {
    super();
    document.addEventListener('section:rendered', this._onSectionRendered);
    window.addEventListener('pageshow', this._onPageShow);
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this._handleMediaQueryChange);
    window.removeEventListener('pageshow', this._onPageShow);
    if (this.#contentObserver) {
      this.#contentObserver.disconnect();
      this.#contentObserver = null;
    }
    if (this.#yotpoRefreshTimeout) {
      clearTimeout(this.#yotpoRefreshTimeout);
      this.#yotpoRefreshTimeout = null;
    }
    document.removeEventListener('section:rendered', this._onSectionRendered);
  }

  _onPageShow = (event) => {
    if (!event.persisted) return;
    const page = this._getCurrentPageFromURL();
    if (page <= 1) return;
    this.beginLoading();
    this._updateButtonStateFromURL();
    this._loadAllPreviousPages();
  };

  _onSectionRendered = async (event) => {
    const id = event?.detail?.sectionId;
    if (!id || id !== this.sectionId) return;

    this.#allProductsLoaded = false;
    this.seenTitles = new Set();
    this.seenProductUrls = null;
    this._isInitializing = true;

    const currentPage = this._getCurrentPageFromURL();
    if (currentPage > 1) {
      this.beginLoading();
    }

    const { grid } = this.refs;
    if (grid && !Array.isArray(grid)) {
      grid.querySelectorAll('.product-grid__item[data-hidden]').forEach((el) => {
        el.removeAttribute('data-hidden');
        el.style.display = '';
      });
    }

    this._initializeSeenTitles();
    this._updateButtonStateFromURL();

    if (currentPage > 1) {
      await this._loadAllPreviousPages();
    }

    if (this.showSingleProduct) {
      this.beginLoading();

      const loadMoreBtn = this.querySelector('.load-more-button');
      const totalPages = parseInt(loadMoreBtn?.dataset.totalPages || '1');
      const canFetchMore = !!loadMoreBtn && !loadMoreBtn.disabled && totalPages > 1;
      
      if (!canFetchMore) {
        this.endLoading();
        requestAnimationFrame(() => {
          this._updateProductCount();
        });
        this._isInitializing = false;
        return;
      }

      Promise.resolve().then(() => {
        const visibleCount = this._getVisibleCount();
        const currentPage = this._getCurrentPageFromURL();

        if (currentPage !== 1) {
          this.endLoading();
          requestAnimationFrame(() => {
            this._updateProductCount();
            this.#refreshYotpoWidgets('section-rendered:page>1');
          });
          this._isInitializing = false;
          return;
        }
        
        const shouldTopUp = this._shouldTopUp({ visible: visibleCount, canFetchMore });
        
        if (shouldTopUp) {
          Promise.resolve(this._ensureMinimumProducts())
            .finally(() => {
              this.endLoading();
              requestAnimationFrame(() => {
                this._updateProductCount();
                this.#refreshYotpoWidgets('section-rendered:auto-top-up');
              });
              this._isInitializing = false;
            });
        } else {
          this.endLoading();
          requestAnimationFrame(() => {
            this._updateProductCount();
            this.#refreshYotpoWidgets('section-rendered:no-top-up');
          });
          this._isInitializing = false;
        }
      });
    } else {
      requestAnimationFrame(() => {
        this._updateProductCount();
        this.#refreshYotpoWidgets('section-rendered:standard');
      });
      this._isInitializing = false;
    }
  }

  _initializeSeenTitles() {
    if (!this.showSingleProduct) return;

    const { grid } = this.refs;
    if (!grid || Array.isArray(grid)) return;

    const existingItems = grid.querySelectorAll('.product-grid__item');
    
    existingItems.forEach(item => {
      const dedupKey = item.dataset.dedupKey;
      if (dedupKey) {
        this.seenTitles.add(dedupKey);
      }
    });
  }

  _applyEagerLoading() {
    const { grid } = this.refs;
    if (!grid || Array.isArray(grid)) return;

    const eagerCount = parseInt(grid.dataset.eagerCount || '0');
    if (eagerCount <= 0) return;

    const cards = grid.querySelectorAll('.custom-product-card');
    let applied = 0;
    cards.forEach((card) => {
      if (applied >= eagerCount) return;
      const primary = card.querySelector('.custom-product-card__img-wrap--primary img');
      if (primary) {
        primary.loading = 'eager';
        primary.fetchPriority = 'high';
        applied += 1;
      }
    });
  }

  _deduplicateProducts(items) {
    const uniqueItems = [];
    const isSearchResults = this.hasAttribute('data-is-search-results');
    
    if (isSearchResults && !this.showSingleProduct) {
      if (!this.seenProductUrls) {
        this.seenProductUrls = new Set();
        const { grid } = this.refs;
        if (grid && !Array.isArray(grid)) {
          const existingItems = grid.querySelectorAll('.product-grid__item');
          existingItems.forEach((item) => {
            const link = item.querySelector('a[href*="/products/"]');
            if (link) {
              const url = new URL(link.href, window.location.origin).pathname;
              this.seenProductUrls.add(url);
            }
          });
        }
      }
      
      items.forEach(item => {
        const link = item.querySelector('a[href*="/products/"]');
        if (link) {
          const url = new URL(link.href, window.location.origin).pathname;
          if (!this.seenProductUrls.has(url)) {
            this.seenProductUrls.add(url);
            uniqueItems.push(item);
          }
        } else {
          uniqueItems.push(item);
        }
      });
      
      return uniqueItems;
    }
    
    if (!this.showSingleProduct) return Array.from(items);

    items.forEach(item => {
      const dedupKey = item.dataset.dedupKey;
      
      if (!dedupKey || dedupKey.trim() === '') {
        uniqueItems.push(item);
        return;
      }
      
      if (!this.seenTitles.has(dedupKey)) {
        this.seenTitles.add(dedupKey);
        uniqueItems.push(item);
      }
    });

    return uniqueItems;
  }

  _getVisibleProductCount() {
    const { grid } = this.refs;
    if (!grid || Array.isArray(grid)) return 0;
    return grid.querySelectorAll('.product-grid__item:not([data-hidden])').length;
  }

  _hideExcessProducts(limit = null) {
    const { grid } = this.refs;
    if (!grid || Array.isArray(grid)) return;

    const targetLimit = limit || this.productsPerPage;
    const allItems = grid.querySelectorAll('.product-grid__item');
    
    allItems.forEach((item, index) => {
      if (index >= targetLimit) {
        item.setAttribute('data-hidden', 'true');
        item.style.display = 'none';
      }
    });
    
    this._updateProductCount();
  }

  _showHiddenProducts() {
    const { grid } = this.refs;
    if (!grid || Array.isArray(grid)) return 0;

    const hiddenItems = grid.querySelectorAll('.product-grid__item[data-hidden]');
    const toShow = Math.min(hiddenItems.length, this.productsPerPage);
    
    for (let i = 0; i < toShow; i++) {
      hiddenItems[i].removeAttribute('data-hidden');
      hiddenItems[i].style.display = '';
    }
    
    this._updateProductCount();
    
    return toShow;
  }

  async _ensureMinimumProductsOnLoad() {
    if (!this.showSingleProduct) return;

    const visibleCount = this._getVisibleProductCount();
    
    if (visibleCount >= this.productsPerPage) return;
    
    await this._ensureMinimumProducts();
  }

  async _ensureMinimumProducts() {
    if (!this.showSingleProduct) return false;

    const { loadMoreButton } = this.refs;
    if (!loadMoreButton || Array.isArray(loadMoreButton)) return false;

    const currentPage = parseInt(loadMoreButton.dataset.currentPage || '1');
    const totalPages = parseInt(loadMoreButton.dataset.totalPages || '1');
    const visibleCount = this._getVisibleProductCount();
    const isSearchResults = this.hasAttribute('data-is-search-results');

    if (!isSearchResults && currentPage >= totalPages) {
      this.#allProductsLoaded = true;
      this._updateProductCount();
      loadMoreButton.style.display = 'none';
      return false;
    }
    
    if (isSearchResults && currentPage >= totalPages) {
      return false;
    }

    if (visibleCount >= this.productsPerPage) {
      return false;
    }

    try {
      await this._loadNextPage();
      
      const newVisibleCount = this._getVisibleProductCount();
      
      if (newVisibleCount > this.productsPerPage) {
        this._hideExcessProducts();
        return false;
      }
      
      if (newVisibleCount >= this.productsPerPage) {
        return false;
      }
      
      return await this._ensureMinimumProducts();
    } catch (error) {
      console.error('Error ensuring minimum products:', error);
      return false;
    }
  }

  _setupLoadMoreButton() {
    const loadMoreButton = this.refs.loadMoreButton;
    if (!loadMoreButton || Array.isArray(loadMoreButton)) {
      return;
    }
    
    loadMoreButton.classList.remove('loading');
    loadMoreButton.disabled = false;
    loadMoreButton.removeAttribute('disabled');
    
    const isSearchResults = this.hasAttribute('data-is-search-results');
    const currentPage = parseInt(loadMoreButton.dataset.currentPage || '1');
    const totalPages = parseInt(loadMoreButton.dataset.totalPages || '1');
    
    if (isSearchResults) {
      if (currentPage < totalPages) {
        loadMoreButton.style.display = 'flex';
        loadMoreButton.disabled = false;
        loadMoreButton.removeAttribute('disabled');
      } else {
        loadMoreButton.style.display = 'none';
      }
    } else {
      if (currentPage >= totalPages) {
        loadMoreButton.disabled = true;
        loadMoreButton.setAttribute('disabled', '');
      } else {
        loadMoreButton.disabled = false;
        loadMoreButton.removeAttribute('disabled');
      }
    }

    loadMoreButton.removeEventListener('click', this._handleLoadMoreClick);
    loadMoreButton.addEventListener('click', (e) => {
      e.preventDefault();
      this._handleLoadMoreClick();
    });
  }

  _setupContentObserver() {
    if (this.#contentObserver) {
      this.#contentObserver.disconnect();
    }

    this.#contentObserver = new MutationObserver((mutations) => {
      if (this._isInitializing || this._isUpdatingCount) return;
      
      // Check if any mutations are related to new product cards (not just Yotpo widget updates)
      let hasNewProductCards = false;
      
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          // Check if any added nodes are product cards or contain product cards
          const addedNodes = Array.from(mutation.addedNodes);
          const isProductCardMutation = addedNodes.some(node => {
            if (node.nodeType !== Node.ELEMENT_NODE) return false;
            // Check if it's a product card, not a Yotpo widget update
            const isProductCard = node.classList?.contains('product-grid__item') || 
                                  node.querySelector?.('.product-grid__item') ||
                                  (node.closest && node.closest('.product-grid__item'));
            // Ignore mutations inside Yotpo widget containers
            const isYotpoWidget = node.closest?.('.yotpo-widget-instance') || 
                                  node.closest?.('.yotpo') ||
                                  node.classList?.contains('yotpo-widget-instance') ||
                                  node.classList?.contains('yotpo');
            return isProductCard && !isYotpoWidget;
          });
          
          if (isProductCardMutation) {
            hasNewProductCards = true;
          }
          
          const loadMoreButton = this.refs.loadMoreButton;
          if (loadMoreButton && !Array.isArray(loadMoreButton) && !loadMoreButton.hasAttribute('data-listener-attached')) {
            this._setupLoadMoreButton();
            loadMoreButton.setAttribute('data-listener-attached', '');
            this._updateButtonStateFromURL();
          }

          if (typeof window !== 'undefined' && typeof window.initProductCardSwatches === 'function') {
            window.initProductCardSwatches(this);
          }

          // Rebuild dedup state after content changes
          if (this.showSingleProduct) {
            const { grid } = this.refs;
            if (grid && !Array.isArray(grid)) {
              this.seenTitles = new Set();
              this._initializeSeenTitles();
              requestIdleCallback(() => {
                this._updateProductCount();
                this._updateButtonStateFromURL();
              });
            }
          }
        }
      });
      
      // Only refresh Yotpo if new product cards were added, not if Yotpo is just updating its widgets
      if (hasNewProductCards) {
        this.#refreshYotpoWidgets('mutation-observer');
      }
    });

    this.#contentObserver.observe(this, {
      childList: true,
      subtree: true
    });
  }

  #refreshYotpoWidgets(context = 'observer', attempt = 0) {
    if (typeof window === 'undefined') return;
    const maxAttempts = 5;

    // Prevent refresh if one is already scheduled
    if (attempt === 0 && this.#yotpoRefreshScheduled) {
      return;
    }

    // Check cooldown period - prevent rapid successive refreshes
    const now = Date.now();
    if (attempt === 0 && (now - this.#lastYotpoRefreshTime) < this.#yotpoRefreshCooldown) {
      // Clear any existing timeout and reschedule
      if (this.#yotpoRefreshTimeout) {
        clearTimeout(this.#yotpoRefreshTimeout);
      }
      this.#yotpoRefreshTimeout = setTimeout(() => {
        this.#refreshYotpoWidgets(context, attempt);
      }, this.#yotpoRefreshCooldown - (now - this.#lastYotpoRefreshTime));
      return;
    }

    if (attempt === 0) {
      this.#yotpoRefreshScheduled = true;
    }

    const schedule = typeof requestIdleCallback === 'function' ? requestIdleCallback : (cb) => setTimeout(cb, 0);

    schedule(() => {
      const { yotpo } = window;
      if (!yotpo) {
        if (attempt < maxAttempts) {
          setTimeout(() => this.#refreshYotpoWidgets(context, attempt + 1), 200 * (attempt + 1));
        } else {
          this.#yotpoRefreshScheduled = false;
        }
        return;
      }

      this.#yotpoRefreshScheduled = false;
      this.#lastYotpoRefreshTime = Date.now();

      // Check if widgets are already initialized to avoid unnecessary refresh
      const widgetInstances = document.querySelectorAll('.yotpo-widget-instance');
      const hasInitializedWidgets = Array.from(widgetInstances).some(widget => {
        return widget.querySelector('.yotpo-sr-bottom-line-summary') !== null;
      });

      // Only refresh if widgets aren't already initialized, or if this is a forced refresh
      if (!hasInitializedWidgets || context !== 'connected') {
        if (typeof yotpo.refreshWidgets === 'function') {
          yotpo.refreshWidgets();
          return;
        }

        if (typeof yotpo.initWidgets === 'function') {
          yotpo.initWidgets();
        }
      }
    }, { timeout: 1000 });
  }

  #logLoadMoreEvent(message, data = {}) {
    return;
  }

  // Loads all previous pages in parallel when on page 2+ (dedup mode)
  async _loadAllPreviousPages() {
    const url = new URL(window.location.href);
    const currentPage = this._getCurrentPageFromURL(url);

    if (currentPage <= 1) return;
    
    const { grid } = this.refs;
    if (!grid || Array.isArray(grid)) return;
    
    this.beginLoading();
    
    try {
      const pagePromises = [];
      for (let page = currentPage - 1; page >= 1; page--) {
        const pageUrl = new URL(window.location.href);
        pageUrl.searchParams.set('page', page.toString());
        pageUrl.hash = '';
        
        pagePromises.push(
          sectionRenderer.getSectionHTML(this.sectionId, true, pageUrl)
            .then(pageContent => {
              const parser = new DOMParser();
              const doc = parser.parseFromString(pageContent, 'text/html');
              
              const loadingGrid = doc.querySelector('.product-grid--loading');
              if (loadingGrid) loadingGrid.remove();
              
              const resultsGrid = doc.querySelector('#ResultsGrid');
              const pageProducts = resultsGrid
                ? Array.from(resultsGrid.querySelectorAll('.product-grid__item')).filter(
                    (el) => !el.classList.contains('loading-card')
                  )
                : [];
              
              return { page, products: pageProducts };
            })
        );
      }
      
      const results = await Promise.all(pagePromises);
      results.sort((a, b) => a.page - b.page);
      const byPageDescending = [...results].reverse();
      for (const { page, products } of byPageDescending) {
        if (products.length > 0) {
          const uniqueProducts = this._deduplicateProducts(products);
          if (uniqueProducts.length > 0) {
            const firstRealProduct = grid.querySelector('.product-grid__item');
            const fragment = document.createDocumentFragment();
            uniqueProducts.forEach(product => fragment.appendChild(product));
            if (firstRealProduct) {
              grid.insertBefore(fragment, firstRealProduct);
            } else {
              grid.appendChild(fragment);
            }
            this.#refreshYotpoWidgets('load-previous-pages');
          }
        }
      }
    } catch (error) {
      console.error('Error loading all previous pages:', error);
    } finally {
      this.endLoading();
      this._clearLoadingPreviousPagesAttr();
      requestAnimationFrame(() => {
        this._updateProductCount();
        this._updateButtonVisibility();
      });
    }
  }

  _clearLoadingPreviousPagesAttr() {
    const wrapper = this.querySelector('[data-loading-previous-pages-js-hook]');
    if (wrapper) {
      wrapper.removeAttribute('data-loading-previous-pages');
    }
  }

  _updateProductCount() {
    const grid = this.refs.grid;
    if (!grid || Array.isArray(grid)) return;

    if (grid.classList.contains('hidden')) {
      return;
    }

    const productCountDisplay = this.querySelector('.product-count-display .product-count-text') ||
                                this.querySelector('.product-grid__footer .product-count-text') ||
                                this.querySelector('.product-count-text') ||
                                grid.closest('results-list')?.querySelector('.product-count-display .product-count-text');
    
    if (!productCountDisplay) {
      return;
    }

    const visibleProducts = this._getVisibleCount();
    const gridChildrenCount = grid.children.length;
    const isSearchResults = this.hasAttribute('data-is-search-results');
    
    let countText;
    const templateKey =
      visibleProducts === 1 ? 'product_count_one' : 'product_count_other';

    if (this.showSingleProduct) {
      const template = this._translations[templateKey];
      countText = template
        .replace('{{ num }}', visibleProducts)
        .replace('{{ total }}', visibleProducts);
    } else {
      const totalProducts = parseInt(grid.dataset.totalProducts || '0');
      const template = this._translations[templateKey];
      countText = template
        .replace('{{ num }}', visibleProducts)
        .replace('{{ total }}', totalProducts);
    }
    
    this._isUpdatingCount = true;
    this.#lastCountText = countText;
    
    productCountDisplay.classList.remove('loading');
    productCountDisplay.removeAttribute('aria-busy');
    productCountDisplay.textContent = countText;
    
    if (this.showSingleProduct) {
      const section = this.closest('.shopify-section');
      const filterCountContainers = section?.querySelectorAll('.products-count-wrapper, .products-count-wrapper--mobile');
      if (filterCountContainers) {
        const simpleCountText = this._translations.item_count.replace('__COUNT__', visibleProducts);
        filterCountContainers.forEach(container => {
          const wrapper = container.querySelector('span');
          if (wrapper) {
            wrapper.textContent = simpleCountText;
            container.classList.remove('loading');
          }
        });
      }
    }
    
    this._updateButtonVisibility();
    
    setTimeout(() => {
      this._isUpdatingCount = false;
    }, 0);
  }

  _updateButtonVisibility() {
    const { loadMoreButton } = this.refs;
    if (!loadMoreButton || Array.isArray(loadMoreButton)) {
      return;
    }

    const currentPage = parseInt(loadMoreButton.dataset.currentPage || '1');
    const totalPagesFromBtn = parseInt(loadMoreButton.dataset.totalPages || '1');
    const lastPageFromGrid = parseInt(this.refs.grid?.dataset?.lastPage || '1');
    const totalPages = Math.min(totalPagesFromBtn, lastPageFromGrid);
    
    const isSearchResults = this.hasAttribute('data-is-search-results');
    
    if (isSearchResults) {
      if (currentPage >= totalPages) {
        loadMoreButton.style.display = 'none';
        loadMoreButton.disabled = true;
        loadMoreButton.setAttribute('disabled', '');
        return;
      }
      loadMoreButton.style.display = 'flex';
      loadMoreButton.disabled = false;
      loadMoreButton.removeAttribute('disabled');
      return;
    }
    
    if (currentPage >= totalPages || this.#allProductsLoaded) {
      if (loadMoreButton.style.display !== 'none') {
        loadMoreButton.style.display = 'none';
      }
      return;
    }
    
    if (loadMoreButton.style.display !== 'block') {
      loadMoreButton.style.display = 'block';
    }
  }

  _updateButtonStateFromURL() {
    const loadMoreButton = this.refs.loadMoreButton;
    if (!loadMoreButton || Array.isArray(loadMoreButton)) {
      return;
    }
    
    const url = new URL(window.location.href);
    const currentPageFromURL = this._getCurrentPageFromURL(url);
    const totalPages = parseInt(loadMoreButton.dataset.totalPages || '1', 10);
    const isSearchResults = this.hasAttribute('data-is-search-results');

    this._isUpdatingCount = true;
    
    loadMoreButton.dataset.currentPage = currentPageFromURL.toString();
    const nextPageFromURL = Math.min(currentPageFromURL + 1, totalPages);
    loadMoreButton.dataset.nextPage = nextPageFromURL.toString();

    if (isSearchResults) {
      if (currentPageFromURL >= totalPages) {
        loadMoreButton.style.display = 'none';
      } else {
        loadMoreButton.disabled = false;
        loadMoreButton.removeAttribute('disabled');
        loadMoreButton.style.display = 'flex';
        const loadMoreText = loadMoreButton.dataset.loadMoreText;
        const textElement = loadMoreButton.querySelector('.load-more-button__text');
        if (textElement) {
          textElement.textContent = loadMoreText;
        }
      }
    } else {
      if (this.#allProductsLoaded) {
        loadMoreButton.style.display = 'none';
      } else if (currentPageFromURL >= totalPages) {
        loadMoreButton.style.display = 'none';
      } else {
        loadMoreButton.disabled = false;
        loadMoreButton.style.display = 'flex';
        const loadMoreText = loadMoreButton.dataset.loadMoreText;
        const textElement = loadMoreButton.querySelector('.load-more-button__text');
        if (textElement) {
          textElement.textContent = loadMoreText;
        }
      }
    }
    
    setTimeout(() => {
      this._isUpdatingCount = false;
    }, 0);
  }

  _getTranslations() {
    if (this._translations) return this._translations;

    const themeTranslations =
      (typeof window !== 'undefined' && window.Theme && window.Theme.translations) || {};

    return {
      product_count_one:
        typeof themeTranslations.product_count_one === 'string'
          ? themeTranslations.product_count_one
          : DEFAULT_TRANSLATIONS.product_count_one,
      product_count_other:
        typeof themeTranslations.product_count_other === 'string'
          ? themeTranslations.product_count_other
          : DEFAULT_TRANSLATIONS.product_count_other,
      item_count:
        typeof themeTranslations.item_count === 'string'
          ? themeTranslations.item_count
          : DEFAULT_TRANSLATIONS.item_count,
    };
  }

  async _loadNextPage() {
    const { loadMoreButton, grid } = this.refs;
    if (!loadMoreButton || !grid || Array.isArray(loadMoreButton) || Array.isArray(grid)) {
      return;
    }

    const totalPages = parseInt(loadMoreButton.dataset.totalPages || '1');
    const currentPage = parseInt(loadMoreButton.dataset.currentPage || '1');
    // Use data-next-page as single source of truth so re-renders/races can't change which page we fetch
    const pageToFetch = parseInt(loadMoreButton.dataset.nextPage || String(currentPage + 1), 10);
    const nextPage = Math.min(Math.max(1, pageToFetch), totalPages);
    const isSearchResults = this.hasAttribute('data-is-search-results');

    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    url.searchParams.set('page', nextPage.toString());
    url.hash = '';

    this.#logLoadMoreEvent('loadNextPage:start', { nextPage, totalPages });

    try {
      grid.setAttribute('aria-busy', 'true');
      const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, url);
      
      const parser = new DOMParser();
      const doc = parser.parseFromString(pageContent, 'text/html');
      
      const loadingGrid = doc.querySelector('.product-grid--loading');
      if (loadingGrid) loadingGrid.remove();
      
      const resultsGrid = doc.querySelector('#ResultsGrid');
      const newItems = resultsGrid
        ? Array.from(resultsGrid.querySelectorAll('.product-grid__item')).filter(
            (el) => !el.classList.contains('loading-card')
          )
        : [];
      
      loadMoreButton.dataset.currentPage = nextPage.toString();
      const nextPageAfter = Math.min(nextPage + 1, totalPages);
      loadMoreButton.dataset.nextPage = nextPageAfter.toString();

      if (newItems.length > 0) {
        const uniqueItems = this._deduplicateProducts(newItems);
        const pageSize = Number(this.productsPerPage) || 24;
        const itemsToAdd = uniqueItems.slice(0, pageSize);
        if (uniqueItems.length > pageSize) {
          console.warn('[ResultsList] Load more: server returned %s items for page %s; appending only %s (productsPerPage=%s). Fix section pagination to use %s per page.', uniqueItems.length, nextPage, itemsToAdd.length, this.productsPerPage, pageSize);
        }
        
        if (itemsToAdd.length === 0) {
          this.#logLoadMoreEvent('loadNextPage:dedup-removed-all', { nextPage, totalPages, fetched: newItems.length });
          if (isSearchResults) {
            if (nextPage >= totalPages) {
              this.#allProductsLoaded = true;
              loadMoreButton.style.display = 'none';
              loadMoreButton.disabled = true;
              loadMoreButton.setAttribute('disabled', '');
            } else {
              this._updateButtonVisibility();
            }
          } else {
            if (nextPage >= totalPages) {
              this.#allProductsLoaded = true;
              loadMoreButton.style.display = 'none';
              loadMoreButton.disabled = true;
              loadMoreButton.setAttribute('disabled', '');
            }
          }
          requestAnimationFrame(() => {
            this._updateProductCount();
          });
          return;
        }

        if (itemsToAdd.length > 0) {
          grid.append(...itemsToAdd);
          this.#logLoadMoreEvent('loadNextPage:appended', { appended: itemsToAdd.length, nextPage, totalPages });
        }

        if (typeof window !== 'undefined' && typeof window.initProductCardSwatches === 'function') {
          window.initProductCardSwatches(grid);
        }
        
        this.#refreshYotpoWidgets('load-next-page');
        
        if (isSearchResults) {
          if (nextPage >= totalPages) {
            this.#allProductsLoaded = true;
            loadMoreButton.style.display = 'none';
            loadMoreButton.disabled = true;
            loadMoreButton.setAttribute('disabled', '');
          } else {
            this._updateButtonVisibility();
          }
          
          const searchResultsElement = this.closest('search-results');
          if (searchResultsElement && doc) {
            const fetchedSearchResults = doc.querySelector('search-results');
            if (fetchedSearchResults) {
              searchResultsElement.dispatchEvent(new CustomEvent('page-loaded', {
                detail: {
                  page: nextPage,
                  fetchedElement: fetchedSearchResults
                },
                bubbles: true
              }));
            }
          }
        }
        setTimeout(() => {
          requestAnimationFrame(() => {
            this._updateProductCount();
          });
        }, 100);
      } else {
        this.#logLoadMoreEvent('loadNextPage:no-items', { nextPage, totalPages });
        if (isSearchResults) {
          if (nextPage >= totalPages) {
            this.#allProductsLoaded = true;
            loadMoreButton.style.display = 'none';
            loadMoreButton.disabled = true;
            loadMoreButton.setAttribute('disabled', '');
          } else {
            this._updateButtonVisibility();
          }
        } else {
          this.#allProductsLoaded = true;
          loadMoreButton.style.display = 'none';
          loadMoreButton.disabled = true;
          loadMoreButton.setAttribute('disabled', '');
        }
        requestAnimationFrame(() => {
          this._updateProductCount();
        });
      }
      
    } catch (error) {
      this.#logLoadMoreEvent('loadNextPage:error', { nextPage, totalPages, error });
      console.error('Error loading next page:', error);
      throw error;
    }
    finally {
      grid.removeAttribute('aria-busy');
      this._announceLoaded();
    }
  }

  _handleLoadMoreClick = async () => {
    const { loadMoreButton, grid } = this.refs;

    if (!loadMoreButton || !grid || Array.isArray(loadMoreButton) || Array.isArray(grid)) {
      return;
    }

    // Cooldown: ignore second run from same gesture (double listener or double-fire) so URL stays at page=2
    const now = Date.now();
    if (now - this.#lastLoadMoreFinishedAt < ResultsList.LOAD_MORE_COOLDOWN_MS) {
      return;
    }

    if (this.#isHandlingLoadMore) {
      return;
    }
    this.#isHandlingLoadMore = true;

    if (loadMoreButton.classList.contains('loading')) {
      loadMoreButton.classList.remove('loading');
      loadMoreButton.disabled = false;
      loadMoreButton.removeAttribute('disabled');
    }

    // Store current scroll position before any async operations
    const scrollPosition = window.scrollY;

    const currentPage = parseInt(loadMoreButton.dataset.currentPage || '1');
    const totalPages = parseInt(loadMoreButton.dataset.totalPages || '1');
    const currentVisibleCount = this._getVisibleProductCount();
    const targetVisibleCount = currentVisibleCount + this.productsPerPage;
    const isSearchResults = this.hasAttribute('data-is-search-results');

    this.#logLoadMoreEvent('handleLoadMore:start', {
      currentPage,
      totalPages,
      currentVisibleCount,
      targetVisibleCount,
    });

    // Hide button immediately for better perceived performance
    loadMoreButton.style.display = 'none';
    loadMoreButton.classList.add('loading');
    loadMoreButton.disabled = true;
    this.beginLoading();

    try {
      if (this.showSingleProduct) {
        const hiddenItems = grid.querySelectorAll('.product-grid__item[data-hidden]');
        if (hiddenItems.length > 0) {
          this._showHiddenProducts();
        }

        while (this._getVisibleProductCount() < targetVisibleCount) {
          const currentPageNow = parseInt(loadMoreButton.dataset.currentPage);
          
          if (currentPageNow >= totalPages) {
            break;
          }
          
          await this._loadNextPage();
          
          const afterFetch = this._getVisibleProductCount();
          
          if (afterFetch > targetVisibleCount) {
            this._hideExcessProducts(targetVisibleCount);
            break;
          }
          
          if (afterFetch >= targetVisibleCount) {
            break;
          }
        }
      } else {
        const currentPageNow = parseInt(loadMoreButton.dataset.currentPage);
        if (currentPageNow < totalPages) {
          await this._loadNextPage();
        }
      }

      const finalPage = parseInt(loadMoreButton.dataset.currentPage);

      this._updateButtonVisibility();
      
      if (isSearchResults) {
        if (finalPage >= totalPages) {
          this.#allProductsLoaded = true;
          const allLoadedText = loadMoreButton.dataset.allLoadedText || '';
          const textElement = loadMoreButton.querySelector('.load-more-button__text');
          if (textElement && allLoadedText) textElement.textContent = allLoadedText;
          loadMoreButton.style.display = 'none';
          loadMoreButton.disabled = true;
          loadMoreButton.setAttribute('disabled', '');
        } else {
          loadMoreButton.disabled = false;
          loadMoreButton.removeAttribute('disabled');
          loadMoreButton.style.display = 'flex';
        }
      } else {
        if (finalPage >= totalPages) {
          this.#allProductsLoaded = true;
          const allLoadedText = loadMoreButton.dataset.allLoadedText || '';
          const textElement = loadMoreButton.querySelector('.load-more-button__text');
          if (textElement && allLoadedText) textElement.textContent = allLoadedText;
          loadMoreButton.style.display = 'none';
        } else {
          if (this.showSingleProduct) {
            this._checkNextPageForUniqueProducts(finalPage + 1, totalPages)
              .then((hasUniqueProducts) => {
                if (hasUniqueProducts) {
                  this.#logLoadMoreEvent('handleLoadMore:next-page-has-unique', { checkPage: finalPage + 1 });
                  loadMoreButton.disabled = false;
                  loadMoreButton.style.display = 'block';
                } else {
                  this.#allProductsLoaded = true;
                  this.#logLoadMoreEvent('handleLoadMore:no-more-unique', { checkPage: finalPage + 1 });
                  loadMoreButton.style.display = 'none';
                }
              });
          } else {
            loadMoreButton.disabled = false;
            loadMoreButton.style.display = 'flex';
          }
        }
      }
    } catch (error) {
      console.error('Error loading more products:', error);
      this.#logLoadMoreEvent('handleLoadMore:error', { error });
    } finally {
      this.#isHandlingLoadMore = false;
      this.#lastLoadMoreFinishedAt = Date.now();

      // Single pushState per click so history stays correct (avoids ending up at ?page=3)
      const pageForUrl = parseInt(loadMoreButton.dataset.currentPage || '1', 10);
      const urlPage = this._getCurrentPageFromURL();
      if (pageForUrl !== urlPage) {
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete('section_id');
        cleanUrl.searchParams.set('page', pageForUrl.toString());
        cleanUrl.hash = '';
        if ('scrollRestoration' in history) {
          const prev = history.scrollRestoration;
          history.scrollRestoration = 'manual';
          history.pushState('', '', cleanUrl.toString());
          requestAnimationFrame(() => {
            window.scrollTo({ top: scrollPosition, behavior: 'instant' });
            history.scrollRestoration = prev;
          });
        } else {
          history.pushState('', '', cleanUrl.toString());
        }
      }

      // Restore scroll position after content loads
      // Use multiple RAF calls and setTimeout to ensure DOM is fully updated
      // This prevents the browser from scrolling to top after pushState
      const restoreScroll = () => {
        const currentScroll = window.scrollY;
        const scrollDiff = Math.abs(currentScroll - scrollPosition);
        
        // If scroll position changed significantly (more than 50px) or scrolled to top, restore it
        // This handles cases where browser scrolls to top after pushState or content load
        if (scrollDiff > 50 || currentScroll < scrollPosition) {
          window.scrollTo({
            top: scrollPosition,
            behavior: 'instant'
          });
        }
      };
      
      // Try multiple times to catch any delayed scroll events
      requestAnimationFrame(() => {
        restoreScroll();
        requestAnimationFrame(() => {
          restoreScroll();
          setTimeout(() => {
            restoreScroll();
          }, 50);
        });
      });
      
      loadMoreButton.classList.remove('loading');
      this.endLoading();
      
      setTimeout(() => {
        requestAnimationFrame(() => {
          this._updateProductCount();
        });
      }, 100);
      
      const currentPageNow = parseInt(loadMoreButton.dataset.currentPage);
      const isSearchResults = this.hasAttribute('data-is-search-results');
      
      if (isSearchResults) {
        if (currentPageNow < totalPages) {
          loadMoreButton.disabled = false;
          loadMoreButton.removeAttribute('disabled');
          loadMoreButton.style.display = 'flex';
        }
      } else {
        if (currentPageNow < totalPages && !this.#allProductsLoaded) {
          loadMoreButton.disabled = false;
          loadMoreButton.removeAttribute('disabled');
        }
      }

      this.#logLoadMoreEvent('handleLoadMore:complete', {
        currentPage: Number(loadMoreButton.dataset.currentPage || currentPage),
        allProductsLoaded: this.#allProductsLoaded,
      });
    }
  };

  async _checkNextPageForUniqueProducts(pageToCheck, totalPages) {
    if (pageToCheck > totalPages) return false;

    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      url.searchParams.set('page', pageToCheck.toString());
      url.hash = '';

      const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, url);
      const parser = new DOMParser();
      const doc = parser.parseFromString(pageContent, 'text/html');
      
      const loadingGrid = doc.querySelector('.product-grid--loading');
      if (loadingGrid) loadingGrid.remove();
      
      const items = doc.querySelectorAll('.product-grid__item');
      
      let uniqueCount = 0;
      items.forEach(item => {
        const dedupKey = item.dataset.dedupKey;
        if (dedupKey && !this.seenTitles.has(dedupKey)) {
          uniqueCount++;
        }
      });
      
      return uniqueCount > 0;
    } catch (error) {
      console.error('Error checking next page for unique products:', error);
      return true;
    }
  }

  _announceLoaded() {
    const region = this.querySelector('[role="status"][aria-live="polite"]');
    if (!region) return;
    const count = this._getVisibleCount();
    const translations = this._getTranslations();
    const templateKey = count === 1 ? 'product_count_one' : 'product_count_other';
    const template = translations[templateKey];
    const countText = template
      .replace('{{ num }}', count)
      .replace('{{ total }}', count);
    region.textContent = countText;
  }

  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this._animateLayoutChange(target.value);
  }

  refreshLoadMoreButton() {
    this._setupLoadMoreButton();
    this._updateButtonStateFromURL();
  }

  _animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this._setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  _setLayout(value) {
    const { grid } = this.refs;
    if (!grid || Array.isArray(grid)) return;
    grid.setAttribute('product-grid-view', value);
  }

  _handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this._setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}